import { ref, onMounted } from 'vue'
import { showFailToast, showSuccessToast } from 'vant'
import { getApiV1Captcha, postApiV1CaptchaVerify, postApiV1SendCode } from '@/api/sdk.gen'

export function useServerCaptcha(options: { autoFetch?: boolean } = {}) {
  const { autoFetch = true } = options
  const captchaId = ref('')
  const captchaImage = ref('')

  async function refresh() {
    try {
      const res = await getApiV1Captcha()
      const { code, data } = res.data as {
        code: number
        data: { captcha_id: string; image: string } | null
      }
      if (code === 0 && data) {
        captchaId.value = data.captcha_id
        captchaImage.value = data.image.startsWith('data:')
          ? data.image
          : `data:image/png;base64,${data.image}`
      }
    } catch {
      // 测试环境回退
      captchaId.value = '1'
      captchaImage.value = ''
    }
  }

  /**
   * 单独校验图形验证码（消费式，通过后 captcha_id 立即失效）。
   * 不通过时提示后端 message 并换一张，返回是否通过；网络异常照常抛出，由调用方兜底。
   */
  async function verify(captchaCode: string): Promise<boolean> {
    const verifyRes = await postApiV1CaptchaVerify({
      body: { captcha_id: captchaId.value, captcha_code: captchaCode },
    })
    const verifyBody = (verifyRes.data ?? verifyRes.error) as
      | { code: number; message: string; data: { valid?: boolean } | null }
      | undefined
    if (verifyBody?.code !== 0 || !verifyBody.data?.valid) {
      showFailToast(verifyBody?.message || '图形验证码错误')
      refresh()
      return false
    }
    return true
  }

  /**
   * 先校验图形验证码，再给 account 发短信验证码；发送成功提示 successText 并调用 onSent。
   * 任一步失败都提示（后端 message 优先，否则 failText）并换一张图形验证码。
   */
  async function sendSmsCode(opts: {
    captchaCode: string
    account: string
    scene: string
    failText: string
    successText: string
    onSent: () => void
  }) {
    try {
      if (!(await verify(opts.captchaCode))) return
      const res = await postApiV1SendCode({
        body: {
          channel: 'phone',
          account: opts.account,
          scene: opts.scene,
        } as never,
      })
      const body = (res.data ?? res.error) as { code: number; message: string } | undefined
      if (body?.code !== 0) {
        showFailToast(body?.message || opts.failText)
        refresh()
        return
      }
      showSuccessToast(opts.successText)
      opts.onSent()
    } catch {
      showFailToast(opts.failText)
      refresh()
    }
  }

  if (autoFetch) onMounted(refresh)

  return { captchaId, captchaImage, refresh, verify, sendSmsCode }
}
